import { hasAnalyticsConsent } from '../utils/cookieConsent';

export const GA_MEASUREMENT_ID = 'G-DBJ2D9N74M';
export const HUBSPOT_PORTAL_ID = '8953044';

const HUBSPOT_SCRIPT_ID = 'hs-script-loader';

let reactGA = null;
let isGaInitialized = false;
let isHubSpotInitialized = false;
let queuedEvents = [];
let queuedUserId = null;
const MAX_QUEUED_EVENTS = 50;

const getDisableFlagKey = (measurementId) => `ga-disable-${measurementId}`;

export const setAnalyticsPaused = (paused) => {
  if (!GA_MEASUREMENT_ID) {
    return;
  }

  window[getDisableFlagKey(GA_MEASUREMENT_ID)] = paused;
};

export const syncAnalyticsConsentState = () => {
  setAnalyticsPaused(!hasAnalyticsConsent());
};

/**
 * Initialize Google Analytics 4 only when the user has accepted cookies.
 */
export const initGA = async (measurementId = GA_MEASUREMENT_ID) => {
  if (!measurementId || !hasAnalyticsConsent()) {
    return false;
  }

  if (isGaInitialized) {
    setAnalyticsPaused(false);
    applyQueuedUserId();
    flushQueuedEvents();
    return true;
  }

  try {
    const { default: ReactGA } = await import('react-ga4');
    reactGA = ReactGA;
    setAnalyticsPaused(false);
    reactGA.initialize(measurementId);
    isGaInitialized = true;
    applyQueuedUserId();
    flushQueuedEvents();
    console.log('GA4 initialized successfully');
    return true;
  } catch (error) {
    console.error('Error initializing GA4:', error);
    return false;
  }
};

/**
 * Track page views only when analytics consent has been granted.
 */
export const trackPageView = (path, title) => {
  if (!hasAnalyticsConsent() || !isGaInitialized || !reactGA) {
    return;
  }

  try {
    reactGA.send({
      hitType: 'pageview',
      page: path,
      title: title || document.title,
    });
    console.log(`GA4 pageview tracked: ${path}`);
  } catch (error) {
    console.error('Error tracking pageview:', error);
  }
};

const compactParams = (params = {}) => {
  const out = {};
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }
    out[key] = value;
  });
  return out;
};

const sendEvent = (eventName, params) => {
  try {
    reactGA.event(eventName, params);
    console.log(`GA4 event tracked: ${eventName}`, params);
  } catch (error) {
    console.error(`Error tracking GA4 event ${eventName}:`, error);
  }
};

const flushQueuedEvents = () => {
  if (!hasAnalyticsConsent() || !isGaInitialized || !reactGA) {
    return;
  }

  const queued = queuedEvents;
  queuedEvents = [];
  queued.forEach(({ eventName, params }) => sendEvent(eventName, params));
};

const applyQueuedUserId = () => {
  if (queuedUserId == null || !hasAnalyticsConsent() || !isGaInitialized || !reactGA) {
    return;
  }

  try {
    reactGA.set({ user_id: queuedUserId });
  } catch (error) {
    console.error('Error setting GA4 user_id:', error);
  }
};

/**
 * Associate subsequent GA4 hits with this user. Queued until GA is ready.
 */
export const setAnalyticsUserId = (userId) => {
  if (userId == null || userId === '') {
    return;
  }

  queuedUserId = String(userId);
  applyQueuedUserId();
};

/**
 * Fire a GA4 custom event. Queued until consent is granted and GA is ready.
 */
export const trackEvent = (eventName, params = {}) => {
  if (!eventName) {
    return;
  }

  const payload = compactParams(params);

  if (!hasAnalyticsConsent() || !isGaInitialized || !reactGA) {
    if (queuedEvents.length < MAX_QUEUED_EVENTS) {
      queuedEvents.push({ eventName, params: payload });
    }
    return;
  }

  sendEvent(eventName, payload);
};

export const clearQueuedAnalyticsEvents = () => {
  queuedEvents = [];
};

const getHubSpotQueue = () => {
  window._hsq = window._hsq || [];
  return window._hsq;
};

const injectHubSpotScript = () => {
  if (document.getElementById(HUBSPOT_SCRIPT_ID)) {
    return;
  }

  const script = document.createElement('script');
  script.id = HUBSPOT_SCRIPT_ID;
  script.type = 'text/javascript';
  script.async = true;
  script.defer = true;
  script.src = `https://js.hs-scripts.com/${HUBSPOT_PORTAL_ID}.js`;
  document.body.appendChild(script);
};

/**
 * Load HubSpot only when the user has accepted optional cookies.
 */
export const initHubSpot = () => {
  if (!hasAnalyticsConsent()) {
    return false;
  }

  if (isHubSpotInitialized) {
    return true;
  }

  try {
    getHubSpotQueue();
    injectHubSpotScript();
    isHubSpotInitialized = true;
    return true;
  } catch (error) {
    console.error('Error initializing HubSpot:', error);
    return false;
  }
};

/**
 * Stop HubSpot from tracking if consent is withdrawn.
 */
export const disableHubSpot = () => {
  if (!window._hsq) {
    return;
  }

  try {
    window._hsq.push(['doNotTrack']);
  } catch (error) {
    console.error('Error disabling HubSpot:', error);
  }
};

/**
 * Tell HubSpot the current SPA path. The tracking script auto-records the
 * first page view on load, so skip trackPageView on that initial render.
 */
export const trackHubSpotPageView = (path, { isInitial = false } = {}) => {
  if (!path || !hasAnalyticsConsent() || !isHubSpotInitialized) {
    return;
  }

  try {
    const _hsq = getHubSpotQueue();
    _hsq.push(['setPath', path]);
    if (!isInitial) {
      _hsq.push(['trackPageView']);
    }
  } catch (error) {
    console.error('Error tracking HubSpot pageview:', error);
  }
};

/**
 * Associate the browser session with a HubSpot contact. Identity is only
 * sent on the next trackPageView / custom event.
 */
export const identifyHubSpotVisitor = ({ email, firstName, lastName } = {}) => {
  if (!email || !hasAnalyticsConsent() || !isHubSpotInitialized) {
    return;
  }

  try {
    const identity = { email };
    if (firstName) {
      identity.firstname = firstName;
    }
    if (lastName) {
      identity.lastname = lastName;
    }

    getHubSpotQueue().push(['identify', identity]);
  } catch (error) {
    console.error('Error identifying HubSpot visitor:', error);
  }
};
